import { localizePathname, type Locale } from '@/lib/i18n-routes';
import {
  categorySlug,
  naceToCategory,
  naceToSubcategory,
  getSubcategoryParent,
  categoryHasChildren,
} from '@/lib/industry-categories';


/**
 * Converts a NACE description into a URL-friendly slug.
 * e.g. "Landwirtschaft, Jagd und damit verbundene Tätigkeiten" → "landwirtschaft-jagd-und-damit-verbundene-taetigkeiten"
 */
export function slugifyNaceDescription(description: string): string {
  return description
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

/**
 * Generate a category page URL with localized slugs.
 *
 * Top-level:   /industry/{cat-slug}
 * Subcategory: /industry/{parent-slug}/{sub-slug}
 */
export function generateCategoryUrl(
  categoryKey: string,
  locale: Locale,
): string {
  const parentKey = getSubcategoryParent(categoryKey);
  const internalPath = parentKey
    ? `/industry/${categorySlug(parentKey, locale)}/${categorySlug(categoryKey, locale)}`
    : `/industry/${categorySlug(categoryKey, locale)}`;
  return localizePathname(internalPath, locale);
}

/**
 * Generate the internal (non-localized) category path. Used by server
 * components and link hrefs that go through <LocaleLink>.
 *
 * Because category slugs are locale-dependent, this requires a locale.
 */
export function generateCategoryInternalPath(
  categoryKey: string,
  locale: string,
): string {
  const parentKey = getSubcategoryParent(categoryKey);
  return parentKey
    ? `/industry/${categorySlug(parentKey, locale)}/${categorySlug(categoryKey, locale)}`
    : `/industry/${categorySlug(categoryKey, locale)}`;
}

/**
 * Generates a SEO-friendly industry URL for a NACE code.
 *
 * New format: /industry/{cat-slug}/{sub-slug}/{code}
 * Falls back to /industry/{cat-slug}/{code} for categories without subs,
 * or /industry/{code} if no category match.
 */
export function generateIndustryUrl(
  code: string,
  description?: string | null,
  locale?: Locale,
): string {
  const lang = locale ?? 'de';
  const catKey = naceToCategory(code);
  const subKey = naceToSubcategory(code);

  let internalPath: string;

  if (catKey && subKey && subKey !== catKey) {
    // Category with subcategory: /industry/{cat}/{sub}/{code}
    internalPath = `/industry/${categorySlug(catKey, lang)}/${categorySlug(subKey, lang)}/${code}`;
  } else if (catKey && !categoryHasChildren(catKey)) {
    // Leaf category (no subcategories): /industry/{cat}/{code}
    internalPath = `/industry/${categorySlug(catKey, lang)}/${code}`;
  } else if (catKey) {
    // Category exists but no sub matched — shouldn't happen, but fallback
    internalPath = `/industry/${categorySlug(catKey, lang)}/${code}`;
  } else {
    // No category match — bare code
    internalPath = `/industry/${code}`;
  }

  if (!locale) return internalPath;
  return localizePathname(internalPath, locale);
}
